import { Alert, Button, Image, SvgIcon } from '@salecycle/ui-component-library';
import useNotifications from 'core/notifications/useNotifications';
import { useCampaignEditorContext } from '../../state/hooks/useCampaignEditorContext';
import styled from 'styled-components';
import { useSaveCampaign } from '../../hooks/useSaveCampaign';
import { useTranslation } from 'react-i18next';
import { T } from '@salecycle/experimental/Typography/Typography';
import { ValidationAlert } from '../ValidationAlert';
import { useUpdateCampaignStatus } from '../../hooks/useUpdateCampaignStatus';
import { OffsiteCampaignStatus } from 'models/offsite/Campaign/LifeCycle';
import { useCampaignValidator } from '../../hooks/useCampaignValidator';
import { formatSendDate } from '../../utils/formatSendDate';
import { useReturnToDashboard } from '../../hooks/useReturnToDashboard';
import { useCallback, useState } from 'react';
import { useDrawer } from '../../state/actions/useDrawer';
import { useMutation, useQueryClient } from 'react-query';
import { useFeatureFlags } from 'core/app/AppContext';
import { getBulkRetargetingCampaignEstimateQuery } from 'server/campaigns/queries/getBulkRetargetingCampaignEstimate/getBulkRetargetingCampaignEstimate';
import { timeUntilEndOfDayInMilliseconds } from 'sections/utils/dates';
import { useParams } from 'react-router';
import { CampaignEditorState } from '../../state/CampaignEditorState';
import { mapStateToCampaign } from '../../infrastructure/mapStateToCampaign';

const Wrapper = styled.div`
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing4};
`;

const HorizontalBox = styled.div`
  display: flex;
  align-items: start;
  gap: 16px;
`;

const Centered = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: ${({ theme }) => theme.designToken.spacing4};
`;

const Spacing = styled.div<{ spacing: 'spacing3' | 'spacing2' }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, spacing }) => theme.designToken[spacing]};
`;

export const CampaignDrawerBody = () => {
  const { t } = useTranslation('campaigns');
  const { state } = useCampaignEditorContext();
  const campaignValidation = useCampaignValidator();
  const { saveCampaign, isLoading: isSaving } = useSaveCampaign();
  const { showNotification } = useNotifications();
  const { updateStatus, isLoading: isUpdatingStatus } = useUpdateCampaignStatus();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setDrawer] = useDrawer();
  const returnToDashboard = useReturnToDashboard();
  const { flagEnabled } = useFeatureFlags();
  const estimationFeatureEnabled: boolean = flagEnabled('ld-bulk-retargeting-estimation', false);
  const isFeatureFlagEnabled = flagEnabled('ld-bulk-date-pickers');

  const handeOnUnschedule = useCallback(async () => {
    await updateStatus(OffsiteCampaignStatus.Draft);
    showNotification(t('bulkRetargetingCampaigns.editor.unscheduledNotification'), 'normal');
    setDrawer(false);
  }, [setDrawer, showNotification, t, updateStatus]);

  switch (state.status) {
    case OffsiteCampaignStatus.Draft:
      return (
        <Wrapper>
          <Spacing spacing="spacing3">
            <T variant="headingH2">{t('bulkRetargetingCampaigns.editor.review.heading')}</T>
            <T variant="body1">{t('bulkRetargetingCampaigns.editor.review.description')}</T>
          </Spacing>
          {state.hasUnsavedChanges && (
            <Spacing spacing="spacing3">
              <Alert heading={t('bulkRetargetingCampaigns.editor.unsavedChanges.heading')} variant="warning">
                <p>{t('bulkRetargetingCampaigns.editor.unsavedChanges.saveDescription')}</p>
              </Alert>
              <div>
                <Button
                  variation="secondary"
                  onClick={saveCampaign}
                  disabled={isSaving}
                  leftSideIcon={<SvgIcon name="arrows-rotate" />}
                >
                  {t('bulkRetargetingCampaigns.editor.unsavedChanges.saveAction')}
                </Button>
              </div>
            </Spacing>
          )}
          <Spacing spacing="spacing2">
            <ValidationAlert
              heading={t('bulkRetargetingCampaigns.editor.tabs.audience')}
              {...campaignValidation.audience}
            />
            {estimationFeatureEnabled && (
              <ForecastPanel campaignEditorState={state} isFeatureFlagEnabled={isFeatureFlagEnabled} />
            )}
            <ValidationAlert
              heading={t('bulkRetargetingCampaigns.editor.tabs.content')}
              {...campaignValidation.content}
            />
            <ValidationAlert
              heading={t('bulkRetargetingCampaigns.editor.tabs.scheduling')}
              {...campaignValidation.scheduling}
              successMessage={
                state.campaign.sendDate
                  ? t('bulkRetargetingCampaigns.editor.review.scheduling.dateSuccessMessage', {
                      date: formatSendDate(state.campaign.sendDate),
                    })
                  : t('bulkRetargetingCampaigns.editor.review.scheduling.immediateSuccessMessage')
              }
            />
          </Spacing>
        </Wrapper>
      );

    case OffsiteCampaignStatus.Scheduled:
      const key = state.campaign?.sendDate ? 'scheduled' : 'immediate';
      return (
        <Wrapper>
          <Centered>
            <Image src={`/images/${state.campaign?.sendDate ? 'scheduled' : 'sending'}.svg`} />
          </Centered>
          <Spacing spacing="spacing3">
            <T variant="headingH2">{t(`bulkRetargetingCampaigns.editor.launched.${key}.heading`)}</T>
            <T variant="body1">
              {t(`bulkRetargetingCampaigns.editor.launched.${key}.description`, {
                date: state.campaign?.sendDate ? formatSendDate(state.campaign.sendDate) : undefined,
              })}
            </T>
          </Spacing>
          <HorizontalBox>
            <Button aria-label={t(`bulkRetargetingCampaigns.editor.returnToDashboard`)} onClick={returnToDashboard}>
              {t(`bulkRetargetingCampaigns.editor.returnToDashboard`)}
            </Button>
            {state.campaign?.sendDate && (
              <Button variation="secondary" onClick={handeOnUnschedule} disabled={isUpdatingStatus}>
                {t(`bulkRetargetingCampaigns.editor.launched.scheduled.action`)}
              </Button>
            )}
          </HorizontalBox>
        </Wrapper>
      );

    case OffsiteCampaignStatus.Triggered:
      return (
        <Wrapper>
          <Centered>
            <Image src={`/images/sending.svg`} />
          </Centered>
          <Spacing spacing="spacing3">
            <T variant="headingH2">{t(`bulkRetargetingCampaigns.editor.launched.triggered.heading`)}</T>
            <T variant="body1">{t(`bulkRetargetingCampaigns.editor.launched.triggered.description`)}</T>
          </Spacing>
          <HorizontalBox>
            <Button aria-label={t(`bulkRetargetingCampaigns.editor.returnToDashboard`)} onClick={returnToDashboard}>
              {t(`bulkRetargetingCampaigns.editor.returnToDashboard`)}
            </Button>
          </HorizontalBox>
        </Wrapper>
      );

    default:
      return null;
  }
};

const ForecastPanel = ({
  campaignEditorState,
  isFeatureFlagEnabled,
}: {
  campaignEditorState: CampaignEditorState;
  isFeatureFlagEnabled: boolean;
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('campaigns');
  const [estimate, setEstimate] = useState<number | undefined>();
  const { siteId, campaignId } = useParams<{ siteId: string; campaignId: string }>();

  const fetchData = async () => {
    const data = await queryClient.fetchQuery({
      queryKey: [campaignEditorState.campaign],
      queryFn: () =>
        getBulkRetargetingCampaignEstimateQuery({
          campaign: mapStateToCampaign(siteId, campaignId, campaignEditorState, t, isFeatureFlagEnabled),
        }),
      staleTime: timeUntilEndOfDayInMilliseconds(),
    });
    setEstimate(data);
  };

  const { mutate, error, isLoading } = useMutation({
    mutationFn: fetchData,
  });

  const estimateIsStale = queryClient.getQueryData([campaignEditorState.campaign]) === undefined;

  return (
    <HorizontalBox style={{ alignItems: 'center' }}>
      <Button
        leftSideIcon={<SvgIcon name="users" />}
        variation="secondary"
        onClick={() => mutate()}
        // Need to set the width to be fit-content due to flexbox
        style={{ width: 'fit-content' }}
      >
        {t('bulkRetargetingCampaigns.editor.review.forecast.buttonLabel')}
      </Button>
      {isLoading && <p>Loading...</p>}
      {error && <p>Error</p>}
      {(estimate || estimate === 0) && !estimateIsStale && (
        <T>{estimate.toLocaleString() + ' ' + t('bulkRetargetingCampaigns.editor.review.forecast.message')}</T>
      )}
    </HorizontalBox>
  );
};
